import React, { useState, useEffect } from 'react';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { Switch } from '../components/Switch';
import { getClinicConfig, saveClinicConfig, checkWhatsappStatus, getWhatsappQrCode, logoutWhatsapp } from '../services/storageService';
import { ClinicConfig, DEFAULT_CONFIG, Attendance } from '../types';
import { attendanceLabel, validateConfig } from '../utils/schedulingRules';
import { formatDateBr } from '../utils/dateUtils';
import { MessageCenter } from '../components/MessageCenter';
import { useToast } from '../components/Toast';

export type SettingsTab = 'general' | 'blocks' | 'integrations' | 'messages';

export const AdminSettings: React.FC<{ section?: SettingsTab }> = ({ section }) => {
    const { addToast } = useToast();
    const [config, setConfig] = useState<ClinicConfig>(DEFAULT_CONFIG);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<SettingsTab>('general');
    const [newBlockedDate, setNewBlockedDate] = useState('');
    const [newBlockedTime, setNewBlockedTime] = useState('');
    const [exceptionDate, setExceptionDate] = useState('');
    const [exceptionAttendance, setExceptionAttendance] = useState<Attendance[]>(['online']);
    const [saving, setSaving] = useState(false);
    const [loadError, setLoadError] = useState(false);
    useEffect(() => { if (section) setActiveTab(section); }, [section]);

    // WhatsApp Logic State
    const [waLoading, setWaLoading] = useState(false);
    const [waStatus, setWaStatus] = useState<'unknown' | 'open' | 'close' | 'connecting'>('unknown');
    const [qrCode, setQrCode] = useState<string | null>(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const cfg = await getClinicConfig();

                // Deep merge to ensure all properties exist even if DB is old
                const mergedConfig: ClinicConfig = {
                    ...DEFAULT_CONFIG,
                    ...cfg,
                    whatsappConfig: { ...DEFAULT_CONFIG.whatsappConfig, ...(cfg.whatsappConfig || {}) },
                    messageTemplates: cfg.messageTemplates,
                    reminderSettings: cfg.reminderSettings,
                };

                setConfig(mergedConfig);

                // Auto-check status if enabled
                if (mergedConfig.whatsappConfig.enabled && mergedConfig.whatsappConfig.instanceUrl) {
                    autoCheckStatus(mergedConfig.whatsappConfig);
                }
            } catch (e) {
                console.error("Erro config", e);
                addToast("Erro ao carregar configurações", "error");
                setLoadError(true);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [addToast]);

    const autoCheckStatus = async (waConfig: ClinicConfig['whatsappConfig']) => {
        try {
            setWaLoading(true);
            const data = await checkWhatsappStatus(waConfig);
            const state = data?.instance?.state || data?.state || 'close';
            setWaStatus(state);
        } catch (e) {
            // Silent error on auto-check
        } finally {
            setWaLoading(false);
        }
    };

    // Save Config function
    const handleConfigSave = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        saveConfigToBackend(config);
    };

    const saveConfigToBackend = async (newConfig: ClinicConfig) => {
        const error = validateConfig(newConfig);
        if (error) { addToast(error, 'error'); return false; }
        if (saving) return false;
        setSaving(true);
        try {
            await saveClinicConfig(newConfig);
            addToast('Configurações salvas com sucesso!', 'success');

            // Re-check status if on integration tab
            if (activeTab === 'integrations' && newConfig.whatsappConfig.enabled) {
                autoCheckStatus(newConfig.whatsappConfig);
            }
            return true;
        } catch (err) {
            addToast('Erro ao salvar configurações', 'error');
            return false;
        } finally {
            setSaving(false);
        }
    };

    const handleAddBlockedDate = async () => {
        if (!newBlockedDate) return;
        if (config.blockedDates.includes(newBlockedDate)) {
            addToast('Esta data já está bloqueada.', 'info');
            return;
        }
        const updatedConfig = {
            ...config,
            blockedDates: [...config.blockedDates, newBlockedDate].sort()
        };

        // Auto Save
        try {
            await saveClinicConfig(updatedConfig);
            setConfig(updatedConfig);
            setNewBlockedDate('');
            addToast('Data bloqueada e salva!', 'success');
        } catch (e) {
            addToast('Erro ao salvar bloqueio.', 'error');
        }
    };

    const handleRemoveBlockedDate = async (dateToRemove: string) => {
        const updatedConfig = {
            ...config,
            blockedDates: config.blockedDates.filter(d => d !== dateToRemove)
        };

        // Auto Save
        try {
            await saveClinicConfig(updatedConfig);
            setConfig(updatedConfig);
            addToast('Bloqueio removido e salvo!', 'success');
        } catch (e) {
            addToast('Erro ao atualizar bloqueios.', 'error');
        }
    };

    // --- Whatsapp Handlers ---

    const checkConnection = async () => {
        if (!config.whatsappConfig.instanceUrl || !config.whatsappConfig.apiKey) {
            addToast("Preencha a URL e a API Key primeiro.", "error");
            return;
        }
        setWaLoading(true);
        setQrCode(null); // Reset QR if checking status
        try {
            const data = await checkWhatsappStatus(config.whatsappConfig);
            // Evolution returns { instance: { state: "open" } } or similar
            const state = data?.instance?.state || data?.state || 'close';
            setWaStatus(state);
            if (state === 'open') {
                addToast("Conexão ativa!", "success");
            } else {
                addToast("Instância desconectada ou fechada.", "info");
            }
        } catch (e) {
            setWaStatus('close');
            addToast("Erro ao comunicar com a API.", "error");
        } finally {
            setWaLoading(false);
        }
    };

    const handleGenerateQR = async () => {
        setWaLoading(true);
        setQrCode(null);
        try {
            const res = await getWhatsappQrCode(config.whatsappConfig);
            if (res.base64) {
                setQrCode(res.base64);
                setWaStatus('connecting');
                addToast("QR Code gerado com sucesso.", "success");
            } else if (res.instance?.state === 'open' || res.state === 'open') {
                setWaStatus('open');
                addToast('Instância já está conectada!', 'info');
            } else {
                addToast('Não foi possível gerar o QR Code.', 'error');
            }
        } catch (e) {
            addToast('Erro ao comunicar com EvolutionAPI.', 'error');
        } finally {
            setWaLoading(false);
        }
    };

    const handleDisconnect = async () => {
        if (!window.confirm('Tem certeza que deseja desconectar o WhatsApp?')) return;
        setWaLoading(true);
        try {
            await logoutWhatsapp(config.whatsappConfig);
            setWaStatus('close');
            setQrCode(null);
            addToast('Desconectado com sucesso.', 'success');
        } catch (e) {
            addToast('Erro ao desconectar.', 'error');
        } finally {
            setWaLoading(false);
        }
    };

    if (loading) return <div className="p-8 text-center text-gray-500">Carregando configurações...</div>;
    if (loadError) return <div role="alert" className="p-8 text-red-700">Não foi possível carregar as configurações. Atualize a página para tentar novamente.</div>;

    const TabButton = ({ id, label, icon, subtitle }: { id: SettingsTab, label: string, icon: string, subtitle: string }) => (
        <button
            onClick={() => setActiveTab(id)}
            className={`w-full text-left p-4 rounded-xl transition-all duration-200 flex items-start gap-4 mb-2 group
        ${activeTab === id
                    ? 'bg-white shadow-md border border-primary/5'
                    : 'hover:bg-white/50 border border-transparent'
                }`}
        >
            <div className={`
        w-10 h-10 rounded-lg flex items-center justify-center text-xl shrink-0 transition-colors
        ${activeTab === id ? 'bg-primary/10 text-primary' : 'bg-gray-100 text-gray-400 group-hover:bg-primary/5'}
      `}>
                {icon}
            </div>
            <div>
                <span className={`block font-semibold text-sm ${activeTab === id ? 'text-primary' : 'text-gray-600'}`}>
                    {label}
                </span>
                <span className="text-[10px] text-gray-400 font-medium tracking-wide block mt-0.5">
                    {subtitle}
                </span>
            </div>
            {activeTab === id && (
                <div className="ml-auto self-center text-accent">
                    →
                </div>
            )}
        </button>
    );

    return (
        <div className="animate-fade-in pb-10">
            {!section && <><h2 className="text-2xl font-serif text-primary mb-1">Configurações</h2><p className="text-xs text-gray-500 mb-8">Gerencie o funcionamento e integrações do sistema.</p></>}

            <div className="flex flex-col md:flex-row gap-8 items-start">

                {/* Sidebar Menu */}
                {!section && <div className="w-full md:w-72 shrink-0 space-y-2">
                    <TabButton
                        id="general"
                        label="Dados & Horários"
                        icon="🕒"
                        subtitle="Empresa e funcionamento"
                    />
                    <TabButton
                        id="blocks"
                        label="Bloqueios & Feriados"
                        icon="🚫"
                        subtitle="Gerenciar dias de folga"
                    />
                    <TabButton
                        id="integrations"
                        label="Integrações"
                        icon="⚡"
                        subtitle="WhatsApp e Automação"
                    />
                    <TabButton
                        id="messages"
                        label="Mensagens"
                        icon="📩"
                        subtitle="Templates de Envio"
                    />
                </div>}

                {/* Content Area */}
                <div className="flex-1 w-full bg-white rounded-xl shadow-sm border border-gray-100 p-4 md:p-8 min-h-[400px]">
                    <form onSubmit={handleConfigSave}>

                        {/* --- GENERAL SETTINGS --- */}
                        {activeTab === 'general' && (
                            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
                                {/* ... General Inputs (same as before) ... */}
                                <div>
                                    <h3 className="text-lg font-bold text-primary mb-1 flex items-center gap-2">
                                        <span className="text-accent">🕒</span> Horário de Funcionamento
                                    </h3>
                                    <p className="text-xs text-gray-400 mb-6 border-b border-gray-100 pb-4">Defina os horários de abertura e fechamento da agenda.</p>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <Input
                                            label="Horário de Abertura"
                                            type="time"
                                            value={config.startHour}
                                            onChange={e => setConfig({ ...config, startHour: e.target.value })}
                                        />
                                        <Input
                                            label="Horário de Encerramento"
                                            type="time"
                                            value={config.endHour}
                                            onChange={e => setConfig({ ...config, endHour: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <h3 className="text-lg font-bold text-primary mb-1 mt-8 flex items-center gap-2">
                                        <span className="text-accent">⏱️</span> Duração das Sessões
                                    </h3>
                                    <p className="text-xs text-gray-400 mb-6 border-b border-gray-100 pb-4">Configure o tempo de cada atendimento.</p>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <Input
                                            label="Duração da Sessão (minutos)"
                                            type="number"
                                            value={config.slotDurationMinutes}
                                            onChange={e => setConfig({ ...config, slotDurationMinutes: Number(e.target.value) })}
                                        />
                                        <Input
                                            label="Intervalo na Agenda (minutos)"
                                            type="number"
                                            value={config.intervalMinutes}
                                            onChange={e => setConfig({ ...config, intervalMinutes: Number(e.target.value) })}
                                            title="Tempo total ocupado na agenda por cada cliente"
                                        />
                                    </div>
                                </div>

                                <div>
                                    <h3 className="text-lg font-bold text-primary mb-1 mt-8 flex items-center gap-2">
                                        <span className="text-accent">📅</span> Dias de Trabalho
                                    </h3>
                                    <p className="text-xs text-gray-400 mb-6 border-b border-gray-100 pb-4">Selecione os dias da semana que você atende.</p>

                                    <div className="flex gap-3 flex-wrap">
                                        {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map((day, idx) => (
                                            <button
                                                key={idx}
                                                type="button"
                                                onClick={() => {
                                                    const newDays = config.workingDays.includes(idx)
                                                        ? config.workingDays.filter(d => d !== idx)
                                                        : [...config.workingDays, idx].sort();
                                                    setConfig({ ...config, workingDays: newDays });
                                                }}
                                                className={`
                                            w-14 h-14 rounded-lg border transition-all duration-200 font-bold text-sm
                                            ${config.workingDays.includes(idx)
                                                        ? 'bg-primary text-white border-primary shadow-md transform scale-105'
                                                        : 'bg-white text-gray-400 border-gray-200 hover:border-accent hover:text-accent'}
                                        `}
                                            >
                                                {day}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="space-y-4">
                                    <h3 className="text-lg font-bold text-primary">Atendimento por dia da semana</h3>
                                    <p className="text-sm text-gray-500">Defina quando há atendimento Online, Presencial ou ambos. Os horários são compartilhados para evitar reservas duplicadas.</p>
                                    {config.workingDays.map(day => <div key={day} className="flex flex-wrap gap-3 items-center rounded-lg border p-3">
                                        <span className="w-24 text-sm font-medium">{['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'][day]}</span>
                                        {(['online', 'presencial'] as Attendance[]).map(attendance => <label key={attendance} className="checkbox-choice"><input type="checkbox" checked={(config.attendanceByWeekday[day] || ['online']).includes(attendance)} onChange={e => {
                                            const current = config.attendanceByWeekday[day] || ['online'];
                                            setConfig({ ...config, attendanceByWeekday: { ...config.attendanceByWeekday, [day]: e.target.checked ? [...current, attendance] : current.filter(value => value !== attendance) } });
                                        }} />{attendanceLabel(attendance)}</label>)}
                                    </div>)}
                                    <p className="text-xs text-gray-500">Um dia sem opções marcadas fica indisponível para novos agendamentos.</p>
                                </div>
                                <div className="space-y-4">
                                    <h3 className="text-lg font-bold text-primary">Exceções por data</h3>
                                    <p className="text-sm text-gray-500">A exceção substitui a regra semanal. Datas bloqueadas continuam fechadas.</p>
                                    <Input label="Data da exceção" type="date" value={exceptionDate} onChange={e => setExceptionDate(e.target.value)} />
                                    <div className="flex flex-wrap gap-4">{(['online', 'presencial'] as Attendance[]).map(attendance => <label key={attendance} className="checkbox-choice"><input type="checkbox" checked={exceptionAttendance.includes(attendance)} onChange={e => setExceptionAttendance(e.target.checked ? [...exceptionAttendance, attendance] : exceptionAttendance.filter(value => value !== attendance))} />{attendanceLabel(attendance)}</label>)}</div>
                                    <Button type="button" disabled={!exceptionDate} onClick={() => { setConfig({ ...config, attendanceExceptions: { ...config.attendanceExceptions, [exceptionDate]: exceptionAttendance } }); setExceptionDate(''); }}>Adicionar exceção</Button>
                                    {Object.entries(config.attendanceExceptions).sort(([a], [b]) => a.localeCompare(b)).map(([date, options]) => <div key={date} className="flex justify-between items-center gap-3 border rounded-lg p-3 text-sm"><span>{formatDateBr(date)} · {options.length ? options.map(attendanceLabel).join(' / ') : 'Sem atendimento'}</span><button type="button" className="text-red-600" onClick={() => { const next = { ...config.attendanceExceptions }; delete next[date]; setConfig({ ...config, attendanceExceptions: next }); }}>Remover</button></div>)}
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-primary mb-4">Informações e regras para o cliente</h3>
                                    <Input label="Endereço do atendimento presencial" value={config.address} onChange={e => setConfig({ ...config, address: e.target.value })} placeholder="Informe o endereço completo" />
                                    <Input label="Instruções para atendimento online" value={config.onlineInstructions} onChange={e => setConfig({ ...config, onlineInstructions: e.target.value })} />
                                    <Input label="Antecedência mínima para cancelar ou solicitar remarcação (horas)" type="number" min={0} max={720} value={config.cancellationHours} onChange={e => setConfig({ ...config, cancellationHours: Number(e.target.value) })} />
                                </div>
                                <div className="flex justify-end pt-4">
                                    <Button type="submit" variant="accent" disabled={saving} className="px-8 shadow-lg shadow-orange-200">
                                        {saving ? 'Salvando...' : 'Salvar Alterações'}
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* --- BLOCKS SETTINGS --- */}
                        {activeTab === 'blocks' && (
                            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
                                <div className="space-y-3">
                                    <h3 className="text-lg font-bold text-primary">Bloquear horário específico</h3>
                                    <p className="text-sm text-gray-500">O bloqueio impede novas reservas e preserva sessões já agendadas.</p>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4"><Input label="Data" type="date" value={newBlockedDate} onChange={e => setNewBlockedDate(e.target.value)} /><Input label="Horário" type="time" value={newBlockedTime} onChange={e => setNewBlockedTime(e.target.value)} /></div>
                                    <Button type="button" disabled={!newBlockedDate || !newBlockedTime || saving} onClick={async () => {
                                        if (config.blockedSlots.some(slot => slot.date === newBlockedDate && slot.time === newBlockedTime)) return;
                                        const next = { ...config, blockedSlots: [...config.blockedSlots, { date: newBlockedDate, time: newBlockedTime }] };
                                        try { await saveClinicConfig(next); setConfig(next); setNewBlockedTime(''); addToast('Horário bloqueado!', 'success'); } catch { addToast('Erro ao salvar bloqueio.', 'error'); }
                                    }}>Bloquear horário e salvar</Button>
                                    {config.blockedSlots.map(slot => <div key={slot.date + slot.time} className="flex justify-between border rounded-lg p-3 text-sm"><span>{formatDateBr(slot.date)} às {slot.time}</span><button type="button" className="text-red-600" onClick={async () => { const next = { ...config, blockedSlots: config.blockedSlots.filter(value => value.date !== slot.date || value.time !== slot.time) }; try { await saveClinicConfig(next); setConfig(next); } catch { addToast('Erro ao remover bloqueio.', 'error'); } }}>Remover</button></div>)}
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-primary mb-1 flex items-center gap-2">
                                        <span className="text-accent">🚫</span> Bloqueio de Datas
                                    </h3>
                                    <p className="text-xs text-gray-400 mb-6 border-b border-gray-100 pb-4">
                                        Bloqueie dias inteiros para feriados, férias ou imprevistos.
                                    </p>

                                    <div className="bg-gray-50 p-6 rounded-xl border border-gray-100 mb-6">
                                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Adicionar Novo Bloqueio</label>
                                        <div className="flex gap-4 items-center">
                                            <input
                                                type="date"
                                                value={newBlockedDate}
                                                onChange={e => setNewBlockedDate(e.target.value)}
                                                className="flex-1 border border-gray-200 rounded-lg p-2.5 focus:ring-2 focus:ring-accent focus:border-accent outline-none text-primary shadow-sm"
                                            />
                                            <Button type="button" variant="primary" onClick={handleAddBlockedDate} disabled={!newBlockedDate} className="h-full">
                                                Bloquear e Salvar
                                            </Button>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">Datas Bloqueadas Atualmente</label>
                                        {config.blockedDates.length > 0 ? (
                                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                                {config.blockedDates.map(date => (
                                                    <div key={date} className="flex items-center justify-between bg-white px-4 py-3 rounded-lg border border-red-100 shadow-sm hover:shadow-md transition-shadow group">
                                                        <span className="text-red-800 font-medium text-sm">{formatDateBr(date)}</span>
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRemoveBlockedDate(date)}
                                                            className="text-red-300 hover:text-red-600 transition-colors"
                                                            title="Remover bloqueio"
                                                        >
                                                            ✕
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="text-center py-10 bg-white border border-dashed border-gray-200 rounded-xl">
                                                <p className="text-gray-400 text-sm">Nenhuma data bloqueada.</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* --- INTEGRATION SETTINGS --- */}
                        {activeTab === 'integrations' && (
                            <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
                                <div>
                                    <h3 className="text-lg font-bold text-primary mb-1 flex items-center gap-2">
                                        <span className="text-green-600 bg-green-100 p-1 rounded">💬</span> Integração WhatsApp
                                    </h3>
                                    <p className="text-xs text-gray-400 mb-6 border-b border-gray-100 pb-4">
                                        Configure a conexão com a <strong>EvolutionAPI</strong> para envio de lembretes automáticos.
                                    </p>

                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                        {/* Config Form */}
                                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 space-y-4 h-fit">
                                            <div className="flex items-center justify-between mb-4 bg-white p-3 rounded-lg border border-gray-100 shadow-sm">
                                                <div>
                                                    <label className="text-sm font-bold text-primary block">Habilitar no Sistema</label>
                                                    <span className="text-[10px] text-gray-400">Ativar recursos de automação</span>
                                                </div>
                                                <Switch
                                                    checked={config.whatsappConfig?.enabled || false}
                                                    onChange={checked => setConfig({
                                                        ...config,
                                                        whatsappConfig: { ...config.whatsappConfig, enabled: checked }
                                                    })}
                                                />
                                            </div>

                                            <div className={`transition-all duration-300 ${config.whatsappConfig?.enabled ? 'opacity-100' : 'opacity-40 pointer-events-none blur-[1px]'}`}>
                                                <Input
                                                    label="URL da Instância (EvolutionAPI)"
                                                    placeholder="https://api.seuserver.com"
                                                    value={config.whatsappConfig?.instanceUrl || ''}
                                                    onChange={e => setConfig({
                                                        ...config,
                                                        whatsappConfig: { ...config.whatsappConfig, instanceUrl: e.target.value }
                                                    })}
                                                />
                                                <Input
                                                    label="Nome da Instância"
                                                    placeholder="Ex: MinhaInstancia"
                                                    value={config.whatsappConfig?.instanceName || ''}
                                                    onChange={e => setConfig({
                                                        ...config,
                                                        whatsappConfig: { ...config.whatsappConfig, instanceName: e.target.value }
                                                    })}
                                                />
                                                <Input
                                                    label="API Key (Token Global)"
                                                    type="password"
                                                    placeholder="Insira o token de autenticação..."
                                                    value={config.whatsappConfig?.apiKey || ''}
                                                    onChange={e => setConfig({
                                                        ...config,
                                                        whatsappConfig: { ...config.whatsappConfig, apiKey: e.target.value }
                                                    })}
                                                />
                                                <div className="pt-2">
                                                    <Button type="submit" variant="primary" className="w-full">
                                                        Salvar Configuração
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Connection Status Area */}
                                        <div className={`bg-white border-2 border-dashed rounded-xl p-6 flex flex-col items-center justify-center min-h-[300px] transition-colors
                                        ${config.whatsappConfig?.enabled ? 'border-primary/20' : 'border-gray-200 opacity-50'}
                                    `}>
                                            {waLoading ? (
                                                <div className="flex flex-col items-center">
                                                    <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-accent mb-3"></div>
                                                    <span className="text-sm text-gray-500">Comunicando com servidor...</span>
                                                </div>
                                            ) : qrCode ? (
                                                <div className="flex flex-col items-center animate-fade-in">
                                                    <p className="text-sm font-bold text-primary mb-3">Escaneie o QR Code</p>
                                                    <div className="bg-white p-2 rounded shadow-lg border border-gray-100 mb-4">
                                                        <img src={qrCode} alt="WhatsApp QR Code" className="w-48 h-48 object-contain" />
                                                    </div>
                                                    <Button type="button" variant="secondary" onClick={checkConnection} className="text-xs">
                                                        Já escaneei, verificar status
                                                    </Button>
                                                </div>
                                            ) : waStatus === 'open' ? (
                                                <div className="text-center animate-fade-in">
                                                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3 relative">
                                                        <span className="text-3xl">✅</span>
                                                        <span className="absolute -bottom-1 -right-1 flex h-4 w-4">
                                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                                            <span className="relative inline-flex rounded-full h-4 w-4 bg-green-500"></span>
                                                        </span>
                                                    </div>
                                                    <h4 className="font-bold text-green-700 text-lg mb-1">Conectado</h4>
                                                    <p className="text-xs text-gray-500 mb-6">Sua instância está ativa e pronta.</p>

                                                    <div className="flex gap-2 justify-center">
                                                        <Button type="button" variant="outline" onClick={checkConnection} className="text-xs">
                                                            Atualizar Status
                                                        </Button>
                                                        <button
                                                            type="button"
                                                            onClick={handleDisconnect}
                                                            className="text-red-500 hover:bg-red-50 px-4 py-2 rounded text-xs font-medium transition-colors"
                                                        >
                                                            Desconectar
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="text-center">
                                                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                                        <span className="text-3xl grayscale opacity-50">📱</span>
                                                    </div>
                                                    <h4 className="font-bold text-gray-400 text-lg mb-4">Não Conectado</h4>
                                                    <div className="space-y-2">
                                                        <Button
                                                            type="button"
                                                            variant="accent"
                                                            disabled={!config.whatsappConfig?.enabled}
                                                            onClick={handleGenerateQR}
                                                        >
                                                            Gerar QR Code
                                                        </Button>
                                                        <div className="block"></div>
                                                        <button
                                                            type="button"
                                                            className="text-xs text-primary/60 hover:text-primary underline"
                                                            onClick={checkConnection}
                                                            disabled={!config.whatsappConfig?.enabled}
                                                        >
                                                            Verificar Status Atual
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'messages' && <MessageCenter config={config} onChange={setConfig} saving={saving} onSave={() => saveConfigToBackend(config)} />}

                    </form>
                </div>
            </div>
        </div>
    );
};
